import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { adminApiError, adminApiMessage } from '../adminApi';
import { agentLabel } from '../evals/agents';
import { EvalBadge, EvalError } from '../evals/EvalBits';
import { AddId, EvalBudgetField, EvalJudgeFields } from '../evals/EvalJudgeFields';
import { EvalLaunchEstimateView } from '../evals/EvalLaunchEstimate';
import { formatUsd } from '../evals/format';
import {
  EVAL_LAUNCH_MAX_MODELS,
  EVAL_LAUNCH_MAX_REPEATS,
  EVAL_NOTES_MAX_CHARS,
  caseFilterCoverage,
  coverageText,
  defaultLaunchForm,
  demoCoverageText,
  demoQuestionCount,
  expertCaseCoverage,
  expertCoverageText,
  heldOutCount,
  launchErrorText,
  launchHintText,
  launchRequestFromForm,
  launchRequestKey,
  modelSourceText,
  parseCaseFilter,
  testSplitRefusal,
  updateLaunchForm,
  withId,
  type EvalChartsMode,
  type EvalLaunchEstimate,
  type EvalLaunchExpert,
  type EvalLaunchForm,
  type EvalLaunchOptions,
  type EvalLaunchRequest,
} from '../evals/launch';
import { evalLaunchApi } from '../evals/launchApi';
import { runHref } from '../evals/query';
import type { EvalSplitFilter, EvalSuite } from '../evals/types';
import { GuideLink } from '../evals/GuideLink';
import '../../styles/admin-evals-launch.css';

// Launch an eval run from the console (TOM-120, ADR 0248): suite, models,
// repeats, split, judges, case filter and a required budget. The page asks for
// the estimate (POST /v1/admin/evals/estimate, which writes nothing) whenever
// the form changes, shows every refusal it lists in plain words, and launches
// only once the estimate is for the form as it stands and refuses nothing.
// The test split, and a long run over split "all", need a second explicit
// confirmation (confirmTestSplit; ADR 0248 §8). A model with no provider key
// is refused, never dropped from the launch. The agent choice (TOM-533, ADR
// 0366) runs Neo (chat) and the Watch by the suite, or one expert alone over
// its own questions, on its own split; an expert's run has no suite and no
// charts to choose. Neo's run leaves the METS demo questions out unless the
// admin ticks the box that includes them (TOM-196, ADR 0368): they are run by
// hand.

/** How long the form rests before the estimate is asked for again. */
const ESTIMATE_DEBOUNCE_MS = 400;

/** What each split runs; `agent` is who may be tuned on the train cases. */
function splitHelp(split: EvalSplitFilter, agent: string): string {
  switch (split) {
    case 'train':
      return `The train cases only: the ones ${agent} may be tuned on.`;
    case 'test':
      return 'The held-out test cases only. Needs a second confirmation.';
    case 'all':
      return 'Train and held-out test cases together. Needs a second confirmation.';
  }
}

/** The METS demo questions a launch runs, as its confirmation names them (TOM-196). */
function demoRunText(cases: number): string {
  if (cases === 0) return 'the METS demo questions, though the case filter picks none';
  return `${cases} METS demo ${cases === 1 ? 'question' : 'questions'}`;
}

/** The expert the form runs alone, or null for Neo and the Watch (TOM-533). */
function formExpert(options: EvalLaunchOptions, form: EvalLaunchForm): EvalLaunchExpert | null {
  return form.subject === null
    ? null
    : (options.experts.find((e) => e.id === form.subject) ?? null);
}

/** Who the run tests, as its words name it. */
function formAgent(options: EvalLaunchOptions, form: EvalLaunchForm): string {
  const expert = formExpert(options, form);
  return expert === null ? 'Neo' : agentLabel(expert.id);
}

/** The agent choice (TOM-533): Neo and the Watch, or one expert alone over its own questions. */
function AgentField({
  options,
  form,
  set,
}: {
  options: EvalLaunchOptions;
  form: EvalLaunchForm;
  set: (patch: Partial<EvalLaunchForm>) => void;
}) {
  return (
    <fieldset className="eval-launch-section" data-testid="eval-launch-agent">
      <legend>Agent</legend>
      <div className="eval-launch-choices">
        <label className="eval-launch-choice">
          <input
            type="radio"
            name="eval-agent"
            value=""
            checked={form.subject === null}
            data-testid="eval-launch-agent-neo"
            onChange={() => set({ subject: null })}
          />
          <span>
            <strong>Neo (chat) and the Watch</strong>
            <span className="admin-muted eval-sub eval-block">
              Neo&apos;s answers on the short or the long suite, and the Watch&apos;s on the long
              one
            </span>
          </span>
        </label>
        {options.experts.map((e) => (
          <label key={e.id} className="eval-launch-choice">
            <input
              type="radio"
              name="eval-agent"
              value={e.id}
              checked={form.subject === e.id}
              data-testid={`eval-launch-agent-${e.id}`}
              onChange={() => set({ subject: e.id })}
            />
            <span>
              <strong>{agentLabel(e.id)}</strong>
              <span className="admin-muted eval-sub eval-block">
                Alone, without Neo: its own {e.cases.length}{' '}
                {e.cases.length === 1 ? 'question' : 'questions'}
                {e.live ? '' : '. Not live yet'}
              </span>
              <span className="admin-muted eval-sub eval-block">
                <code>{e.version}</code>
              </span>
            </span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

function LaunchFormFields({
  options,
  form,
  set,
}: {
  options: EvalLaunchOptions;
  form: EvalLaunchForm;
  set: (patch: Partial<EvalLaunchForm>) => void;
}) {
  const modelIds = [...options.models.map((m) => m.id), ...form.extraModels];
  const toggle = (all: readonly string[], chosen: readonly string[], id: string, on: boolean) =>
    all.filter((x) => (x === id ? on : chosen.includes(x)));
  const prefixes = parseCaseFilter(form.only);
  // The long cases in the split chosen; the short suite is never split.
  const coverage = caseFilterCoverage(options.cases, form.suite, prefixes, {
    includeDemo: form.includeDemo,
    split: form.split,
  });
  // Each suite's whole selection, on every split, the METS demo questions in
  // it only when included (TOM-196).
  const whole = (suite: EvalSuite) =>
    caseFilterCoverage(options.cases, suite, [], { includeDemo: form.includeDemo });
  const shortCount = whole('short').short;
  const long = whole('long');
  const longCount = long.short + long.questions + long.conditions;
  // An expert alone runs its own questions on its own split (TOM-533).
  const expert = formExpert(options, form);
  const expertCoverage = expert === null ? null : expertCaseCoverage(expert, form.split, prefixes);
  const unmatched = expertCoverage?.unmatched ?? coverage.unmatched;
  // What the run does with the METS demo questions the filter picks (TOM-196).
  const demoText = expert === null ? demoCoverageText(coverage.demo, form.includeDemo) : null;
  const splitShown = form.suite === 'long' || expert !== null;
  const examplePrefix = expert?.cases[0]?.id.split('-').slice(0, 2).join('-');

  return (
    <>
      <AgentField options={options} form={form} set={set} />

      {expert === null ? (
        <fieldset className="eval-launch-section">
          <legend>Suite</legend>
          <div className="eval-launch-choices">
            {(['short', 'long'] as const satisfies readonly EvalSuite[]).map((s) => (
              <label key={s} className="eval-launch-choice">
                <input
                  type="radio"
                  name="eval-suite"
                  value={s}
                  checked={form.suite === s}
                  data-testid={`eval-launch-suite-${s}`}
                  onChange={() => set({ suite: s })}
                />
                <span>
                  <strong>{s === 'short' ? 'Short' : 'Long'}</strong>
                  <span className="admin-muted eval-sub eval-block">
                    {s === 'short'
                      ? `${shortCount} attack and helpful cases`
                      : `${longCount} cases: the short ones, owner questions on recorded moments and Watch conditions`}
                  </span>
                </span>
              </label>
            ))}
          </div>
        </fieldset>
      ) : null}

      <fieldset className="eval-launch-section" data-testid="eval-launch-models">
        <legend>
          Models{' '}
          <span className="admin-muted eval-sub">
            {form.models.length} of at most {EVAL_LAUNCH_MAX_MODELS} chosen
          </span>
        </legend>
        <ul className="eval-launch-options">
          {modelIds.map((id) => {
            const o = options.models.find((m) => m.id === id);
            const checked = form.models.includes(id);
            return (
              <li key={id}>
                <label className="eval-launch-option" data-testid={`eval-launch-model-${id}`}>
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(e) =>
                      set({ models: toggle(modelIds, form.models, id, e.target.checked) })
                    }
                  />
                  <span className="eval-launch-option-text">
                    <span>
                      {o !== undefined && o.label !== o.id ? <strong>{o.label} </strong> : null}
                      <code>{id}</code>
                    </span>
                    <span className="eval-launch-option-tags">
                      <span className="admin-muted eval-sub">
                        {o !== undefined ? modelSourceText(o.source) : 'Added by id'}
                      </span>
                      {o !== undefined && !o.configured ? (
                        <EvalBadge
                          tone="warn"
                          title="Its provider key is not set: a launch with it is refused"
                          testId="eval-launch-no-key"
                        >
                          No provider key
                        </EvalBadge>
                      ) : null}
                    </span>
                  </span>
                </label>
                {o === undefined ? (
                  <button
                    type="button"
                    className="admin-link-button"
                    onClick={() =>
                      set({
                        extraModels: form.extraModels.filter((x) => x !== id),
                        models: form.models.filter((x) => x !== id),
                      })
                    }
                  >
                    Remove
                  </button>
                ) : null}
              </li>
            );
          })}
        </ul>
        <AddId
          label="Another model, by id"
          placeholder="openrouter:vendor/model or anthropic:claude-…"
          disabled={form.models.length >= EVAL_LAUNCH_MAX_MODELS}
          testId="eval-launch-add-model"
          onAdd={(id) =>
            set({
              extraModels: modelIds.includes(id) ? form.extraModels : [...form.extraModels, id],
              models: withId(form.models, id),
            })
          }
        />
      </fieldset>

      <fieldset className="eval-launch-section">
        <legend>Repeats and split</legend>
        <div className="eval-launch-row">
          <label>
            <span className="eval-launch-label">Repeats</span>
            <input
              type="number"
              min={1}
              max={EVAL_LAUNCH_MAX_REPEATS}
              step={1}
              value={form.repeats}
              data-testid="eval-launch-repeats"
              onChange={(e) => set({ repeats: e.target.value })}
            />
          </label>
          {splitShown ? (
            <label>
              <span className="eval-launch-label">Split</span>
              <select
                value={form.split}
                data-testid="eval-launch-split"
                onChange={(e) => set({ split: e.target.value as EvalSplitFilter })}
              >
                <option value="train">Train</option>
                <option value="test">Test (held out)</option>
                <option value="all">All</option>
              </select>
            </label>
          ) : null}
        </div>
        <p className="admin-muted eval-sub">
          Each case runs this many times; a case passes on the majority of its repeats.
          {splitShown ? ` ${splitHelp(form.split, formAgent(options, form))}` : ''}
        </p>
      </fieldset>

      <EvalJudgeFields judges={options.judges} form={form} set={set} />

      <fieldset className="eval-launch-section">
        <legend>Cases</legend>
        <label>
          <span className="eval-launch-label">Case filter (id prefixes)</span>
          <input
            type="text"
            value={form.only}
            placeholder={
              expert !== null
                ? `Every question. Or, e.g., ${examplePrefix ?? ''}`
                : 'Every case. Or, e.g., jb- help-tides'
            }
            data-testid="eval-launch-only"
            onChange={(e) => set({ only: e.target.value })}
          />
        </label>
        {expert === null ? (
          <label className="eval-launch-option" data-testid="eval-launch-demo">
            <input
              type="checkbox"
              checked={form.includeDemo}
              data-testid="eval-launch-include-demo"
              onChange={(e) => set({ includeDemo: e.target.checked })}
            />
            <span>
              Include the METS demo questions
              <span className="admin-muted eval-sub eval-block">
                The {demoQuestionCount(options.cases, form.suite, form.split)} questions from the
                METS demo script. They are normally run by hand, so a run leaves them out unless you
                tick this, whatever the case filter picks.
              </span>
            </span>
          </label>
        ) : null}
        <p className="admin-muted eval-sub" data-testid="eval-launch-coverage">
          Selects{' '}
          {expert !== null && expertCoverage !== null
            ? expertCoverageText(expert, expertCoverage)
            : coverageText(coverage, form.suite)}
          .{demoText !== null ? ` ${demoText}` : null}
          {unmatched.length > 0 ? (
            <span className="eval-error-text"> Nothing starts with {unmatched.join(', ')}.</span>
          ) : null}
        </p>
        <div className="eval-launch-row">
          {form.suite === 'short' ? (
            <label>
              <span className="eval-launch-label">Limit</span>
              <input
                type="number"
                min={1}
                max={1000}
                step={1}
                value={form.limit}
                placeholder="No limit"
                onChange={(e) => set({ limit: e.target.value })}
              />
            </label>
          ) : null}
          {expert === null ? (
            <label>
              <span className="eval-launch-label">Charts</span>
              <select
                value={form.charts}
                onChange={(e) => set({ charts: e.target.value as EvalChartsMode })}
              >
                <option value="auto">Auto (graphics-capable models)</option>
                <option value="on">On for every model</option>
                <option value="off">Off</option>
              </select>
            </label>
          ) : null}
        </div>
      </fieldset>

      <fieldset className="eval-launch-section">
        <legend>Notes</legend>
        <label>
          <span className="eval-launch-label">Why this run (optional)</span>
          <textarea
            value={form.notes}
            maxLength={EVAL_NOTES_MAX_CHARS}
            rows={2}
            onChange={(e) => set({ notes: e.target.value })}
          />
        </label>
      </fieldset>
    </>
  );
}

function TestSplitConfirmation({
  estimate,
  form,
  agent,
  set,
}: {
  estimate: EvalLaunchEstimate | null;
  form: EvalLaunchForm;
  /** Who the held-out cases are kept from tuning: Neo, or the expert (TOM-533). */
  agent: string;
  set: (patch: Partial<EvalLaunchForm>) => void;
}) {
  const refusal = testSplitRefusal(estimate);
  const [count, setCount] = useState<number | null>(null);
  const n = heldOutCount(refusal);
  useEffect(() => {
    if (n !== null) setCount(n);
  }, [n]);
  if (refusal === null && !form.confirmTestSplit) return null;
  const shown = n ?? count;
  return (
    <div className="eval-confirm-test" data-testid="eval-confirm-test">
      <p>
        <strong>The held-out test split.</strong> Its cases are held out so nobody tunes {agent} to
        them: on the run&apos;s page their replies, reasons and rubrics stay hidden, and only ids
        and figures show. Run them only for a deliberate test measurement.
      </p>
      <label className="eval-launch-option">
        <input
          type="checkbox"
          checked={form.confirmTestSplit}
          data-testid="eval-confirm-test-split"
          onChange={(e) => set({ confirmTestSplit: e.target.checked })}
        />
        <span>
          Yes, this run measures the held-out test split
          {shown !== null ? ` (${shown} test ${shown === 1 ? 'case' : 'cases'})` : ''}.
        </span>
      </label>
    </div>
  );
}

export function AdminEvalLaunch() {
  const navigate = useNavigate();
  const [options, setOptions] = useState<EvalLaunchOptions | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [form, setForm] = useState<EvalLaunchForm | null>(null);
  const [estimate, setEstimate] = useState<{ key: string; data: EvalLaunchEstimate } | null>(null);
  const [estimateError, setEstimateError] = useState<string | null>(null);
  const [estimating, setEstimating] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [launching, setLaunching] = useState(false);
  const [launchError, setLaunchError] = useState<string | null>(null);
  const seq = useRef(0);

  useEffect(() => {
    let ignore = false;
    evalLaunchApi
      .options()
      .then((o) => {
        if (ignore) return;
        setOptions(o);
        setForm(defaultLaunchForm(o));
      })
      .catch((err) => {
        if (!ignore) setLoadError(adminApiMessage(err, 'Failed to load the launch options.'));
      });
    return () => {
      ignore = true;
    };
  }, []);

  const built = form !== null && options !== null ? launchRequestFromForm(form, options) : null;
  const key = built !== null && built.ok ? launchRequestKey(built.body) : null;

  useEffect(() => {
    if (key === null) {
      seq.current += 1;
      setEstimating(false);
      return;
    }
    const n = ++seq.current;
    setEstimating(true);
    const timer = window.setTimeout(() => {
      evalLaunchApi
        .estimate(JSON.parse(key) as EvalLaunchRequest)
        .then((data) => {
          if (n !== seq.current) return;
          setEstimate({ key, data });
          setEstimateError(null);
        })
        .catch((err) => {
          if (n !== seq.current) return;
          setEstimate(null);
          setEstimateError(launchErrorText(adminApiError(err)));
        })
        .finally(() => {
          if (n === seq.current) setEstimating(false);
        });
    }, ESTIMATE_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [key]);

  function set(patch: Partial<EvalLaunchForm>) {
    if (options === null) return;
    setConfirming(false);
    setLaunchError(null);
    setForm((f) => (f === null ? f : updateLaunchForm(f, patch, options)));
  }

  const fresh = estimate !== null && key !== null && estimate.key === key && !estimating;
  const shown = estimate !== null && key !== null ? estimate.data : null;
  const canLaunch = fresh && estimate.data.refusals.length === 0;

  // Two clicks, never a form submit: Enter in a field must not launch a run
  // that spends provider money.
  async function launch() {
    if (!canLaunch || !confirming || built === null || !built.ok) return;
    setLaunching(true);
    setLaunchError(null);
    try {
      const res = await evalLaunchApi.launch(built.body);
      navigate(runHref(res.runId));
    } catch (err) {
      setConfirming(false);
      setLaunchError(launchErrorText(adminApiError(err)));
    } finally {
      setLaunching(false);
    }
  }

  const testCount = heldOutCount(testSplitRefusal(shown));

  return (
    <section className="admin-section eval-page eval-launch-page">
      <p className="admin-hint">
        <Link to="/admin/evals/runs">← All runs</Link>
      </p>
      <header className="admin-section-header">
        <h1>Launch an eval run</h1>
        <GuideLink section="launch" />
      </header>
      <p className="admin-hint" data-testid="eval-launch-hint">
        {launchHintText(options?.daily ?? null)}
      </p>

      <EvalError message={loadError} />
      {options === null && loadError === null ? (
        <p className="admin-loading-row">Loading…</p>
      ) : null}

      {options !== null && form !== null ? (
        <form className="eval-launch" onSubmit={(e) => e.preventDefault()} noValidate>
          <div className="eval-launch-form">
            <LaunchFormFields options={options} form={form} set={set} />
          </div>

          <aside className="eval-launch-aside" aria-label="Estimate and launch">
            <h2>Budget and estimate</h2>
            <EvalBudgetField
              maxBudgetUsd={options.maxBudgetUsd}
              value={form.budgetUsd}
              onChange={(budgetUsd) => set({ budgetUsd })}
              testId="eval-launch-budget"
            />
            {built !== null && !built.ok ? (
              <ul className="eval-list eval-launch-problems" data-testid="eval-launch-problems">
                {built.problems.map((p) => (
                  <li key={p}>{p}</li>
                ))}
              </ul>
            ) : null}
            {key !== null && shown === null && estimateError === null ? (
              <p className="admin-loading-row" data-testid="eval-estimating">
                Estimating…
              </p>
            ) : null}
            {estimateError !== null ? (
              <p className="eval-error-text" role="alert" data-testid="eval-estimate-error">
                {estimateError}
              </p>
            ) : null}
            {shown !== null ? <EvalLaunchEstimateView estimate={shown} stale={!fresh} /> : null}

            <TestSplitConfirmation
              estimate={fresh ? shown : null}
              form={form}
              agent={formAgent(options, form)}
              set={set}
            />

            <div className="eval-launch-go">
              {confirming && canLaunch ? (
                <div className="admin-confirm" data-testid="eval-launch-confirm">
                  <span className="admin-confirm-prompt">
                    Launch? This run can spend up to {formatUsd(shown!.spendCeilingUsd)} of provider
                    money (estimate {formatUsd(shown!.estimate.totalUsd)}, budget{' '}
                    {formatUsd(shown!.budgetUsd)})
                    {form.confirmTestSplit ? ', and it measures the held-out test split' : ''}
                    {shown!.demo.included ? `, and it runs ${demoRunText(shown!.demo.cases)}` : ''}.
                  </span>
                  <button
                    type="button"
                    className="admin-button-affirm"
                    disabled={launching}
                    onClick={() => void launch()}
                    data-testid="eval-launch-confirm-button"
                  >
                    {launching ? 'Launching…' : 'Launch'}
                  </button>
                  <button
                    type="button"
                    className="admin-link-button"
                    onClick={() => setConfirming(false)}
                  >
                    Back
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  className="admin-button-affirm"
                  disabled={!canLaunch}
                  onClick={() => setConfirming(true)}
                  data-testid="eval-launch-submit"
                >
                  Launch run…
                </button>
              )}
              {!canLaunch ? (
                <p className="admin-muted eval-sub" data-testid="eval-launch-blocked">
                  {built !== null && !built.ok
                    ? 'Complete the form to launch.'
                    : estimateError !== null && !estimating
                      ? 'The estimate was refused: fix what it says above.'
                      : !fresh
                        ? 'Waiting for the estimate of the form as it stands.'
                        : `Refused until ${shown!.refusals.length === 1 ? 'the item' : 'every item'} above is fixed.${testCount !== null && !form.confirmTestSplit ? ' Confirming the test split clears its item.' : ''}`}
                </p>
              ) : null}
              {launchError !== null ? (
                <p className="eval-error-text" role="alert" data-testid="eval-launch-error">
                  {launchError}
                </p>
              ) : null}
            </div>
          </aside>
        </form>
      ) : null}
    </section>
  );
}
